import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import BaseSchedule from "./BaseSchedule";
import { makeStyles } from "@mui/styles";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import { STATION, ALL_WORKGROUP_DEFAULT_FILTERS } from "../../util/constants";
import StarsSelectInput from "../fundamentals/StarsSelectInput";
import StarsDatePicker from "../fundamentals/StarsDatePicker";
import { THREE_MONTHS_IN_FUTURE, TODAY } from "../../util/dateHelper";
import { convert_Luxon_To_DateFns, getIsoDateFormat } from "../../util/dateTimeFormatter";
import {
  BASE_SCHEDULE_OVERWRITE_WARNING_MESSAGE,
  CREATE_BASE_SCHEDULE_CONFIRMATION_MESSAGE,
  FREEFORM_REASON_CHAR_LIMIT,
  baseScheduleApplyToAllFilterInitialState,
  baseScheduleErrorsInitialState,
  baseScheduleInitialState,
  employeeInfoErrorsInitialState,
} from "../../util/schedule/scheduleConstants";
import { copyJsonObject, sortOnCaseSensitiveTexts } from "../../util/commonUtils";
import {
  validateBaseScheduleComments,
  validateBaseScheduleEndDate,
  validateBaseScheduleStartDate,
  validateNoPartialSchedules,
} from "../../util/schedule/inputValidation";
import { addBaseSchedule } from "../../api/scheduleEditor";
import { getPermanentBidPositionsByContract } from "../../api/commonConfig/bidConfig";
import { getUnionContractGivenWorkgroup } from "../../util/workgroupMapper";
import { Interval } from "luxon";
import { getExcludedAssignmentDays } from "../../util/schedule/scheduleEditorUtils";
import ConfirmationDialog from "../fundamentals/ConfirmationDialog";
import { WARNING_ALERT_TITLE } from "../../util/messageConstants";
import { baseScheduleDailyValidation, isDayIncludedInAssignment } from "../../util/temporaryAssignmentInputValidation";
import { useSelector } from "react-redux";
import { selectUserSession } from "../../slices/userSession/userSessionSlice";
import SuccessDialog from "../fundamentals/SuccessDialog";

const useStyles = makeStyles((theme) => ({
  inputSelect: {
    width: "200px",
    textAlign: "left",
    marginRight: "15px",
    "& .MuiInputBase-root": {
      background: "white",
    },
  },
  inputStation: {
    maxWidth: "17vw",
    textAlign: "left",
    width: "90px",
    marginRight: "15px",
  },
}));

const CustomBaseScheduleDialog = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const [comments, setComments] = useState("");
  const [baseSchedule, setBaseSchedule] = useState(baseScheduleInitialState);
  const [positions, setPositions] = useState([]);
  const [workgroup, setWorkgroup] = useState(props.workgroup);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const [employeeInfoErrors, setEmployeeInfoErrors] = useState(employeeInfoErrorsInitialState);
  const [baseScheduleErrors, setBaseScheduleErrors] = useState(baseScheduleErrorsInitialState);
  const [applyToAllFilterData, setApplyToAllFilterData] = useState(
    copyJsonObject(baseScheduleApplyToAllFilterInitialState)
  );
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [successDialogError, setSuccessDialogError] = useState("");
  const [isSuccessDialogError, setIsSuccessDialogError] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isValidationLoading, setIsValidationLoading] = useState(false);

  useEffect(() => {
    getPositionsByWorkgroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workgroup]);

  useEffect(() => {
    setExcludedAssignmentDays(baseSchedule);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDateRef.current, endDateRef.current]);

  const getPositionsByWorkgroup = () => {
    const contract = getUnionContractGivenWorkgroup(workgroup);
    getPermanentBidPositionsByContract(contract)
      .then((res) => res.json())
      .then((result) => {
        let positions = [];
        result.forEach((pos) => {
          positions.push(...pos.positions);
        });
        positions = [...new Set(positions)]; // remove duplicates
        setPositions(positions);
      });
  };

  const setExcludedAssignmentDays = (schedule) => {
    let currentSchedule = copyJsonObject(schedule);
    if (startDateRef.current !== null && endDateRef.current !== null) {
      const baseScheduleLengthInDays = Interval.fromDateTimes(startDateRef.current, endDateRef.current).length("days");
      if (baseScheduleLengthInDays < 7) {
        let excludedAssignmentDays = getExcludedAssignmentDays(startDateRef.current, endDateRef.current);
        for (const day in currentSchedule) {
          if (currentSchedule[day].isNA === true && !excludedAssignmentDays.includes(day)) {
            currentSchedule[day].start.hr = "";
            currentSchedule[day].start.min = "";
            currentSchedule[day].end.hr = "";
            currentSchedule[day].end.min = "";
            currentSchedule[day].position = "Float";
            currentSchedule[day].assignment = "Float";
            currentSchedule[day].isOff = false;
            currentSchedule[day].isNA = false;
          }
        }

        excludedAssignmentDays.forEach((excludedDay) => {
          currentSchedule[excludedDay].start.hr = "N/A";
          currentSchedule[excludedDay].start.min = "N/A";
          currentSchedule[excludedDay].end.hr = "N/A";
          currentSchedule[excludedDay].end.min = "N/A";
          currentSchedule[excludedDay].position = "N/A";
          currentSchedule[excludedDay].assignment = "N/A";
          currentSchedule[excludedDay].isOff = false;
          currentSchedule[excludedDay].isNA = true;
        });
      } else {
        for (const day in currentSchedule) {
          if (currentSchedule[day].isNA === true) {
            currentSchedule[day].start.hr = "";
            currentSchedule[day].start.min = "";
            currentSchedule[day].end.hr = "";
            currentSchedule[day].end.min = "";
            currentSchedule[day].position = "Float";
            currentSchedule[day].assignment = "Float";
            currentSchedule[day].isOff = false;
            currentSchedule[day].isNA = false;
          }
        }
      }
      setBaseSchedule(copyJsonObject(currentSchedule));
    }
  };

  //#region Input Validations
  const validateDates = () => {
    let employeeInfoErrorValidation = copyJsonObject(employeeInfoErrors);
    if (startDateRef.current) {
      employeeInfoErrorValidation.startDateErrors = validateBaseScheduleStartDate(
        startDateRef.current,
        endDateRef.current
      );
    }
    if (endDateRef.current) {
      employeeInfoErrorValidation.endDateErrors = validateBaseScheduleEndDate(startDateRef.current, endDateRef.current);
    }
    setEmployeeInfoErrors({ ...employeeInfoErrorValidation });
  };

  const validateComments = () => {
    let employeeInfoErrorValidation = copyJsonObject(employeeInfoErrors);
    employeeInfoErrorValidation.commentErrors = validateBaseScheduleComments(comments);
    setEmployeeInfoErrors({ ...employeeInfoErrorValidation });
  };

  const validateEmployeeInfo = () => {
    let employeeInfoErrorValidation = copyJsonObject(employeeInfoErrorsInitialState);
    setEmployeeInfoErrors(copyJsonObject(employeeInfoErrorsInitialState));
    let isFormValid = false;

    employeeInfoErrorValidation.startDateErrors = validateBaseScheduleStartDate(startDate, endDate);
    employeeInfoErrorValidation.endDateErrors = validateBaseScheduleEndDate(startDate, endDate);
    employeeInfoErrorValidation.commentErrors = validateBaseScheduleComments(comments);
    setEmployeeInfoErrors({ ...employeeInfoErrorValidation });

    for (const errorType in employeeInfoErrorValidation) {
      if (employeeInfoErrorValidation[errorType] !== "") {
        return isFormValid;
      }
    }

    isFormValid = true;

    return isFormValid;
  };

  const validateBaseSchedule = () => {
    let isFormValid = false;
    let setErrorsFlag = false;
    let scheduleErrors = copyJsonObject(baseScheduleErrorsInitialState);
    setBaseScheduleErrors(copyJsonObject(baseScheduleErrorsInitialState));

    scheduleErrors.fullScheduleError = validateNoPartialSchedules(baseSchedule);

    if (scheduleErrors.fullScheduleError !== "") {
      setBaseScheduleErrors(copyJsonObject(scheduleErrors));
      return isFormValid;
    }

    const contract = getUnionContractGivenWorkgroup(workgroup);
    for (const day in baseSchedule) {
      if (isDayIncludedInAssignment(baseSchedule[day])) {
        scheduleErrors[day].error = baseScheduleDailyValidation(baseSchedule, day, contract);
        if (scheduleErrors[day].error !== "") {
          setErrorsFlag = true;
        }
      }
    }
    if (setErrorsFlag) {
      setBaseScheduleErrors(copyJsonObject(scheduleErrors));
      return isFormValid;
    }

    isFormValid = true;

    return isFormValid;
  };

  const areAllValidationsPassing = () => {
    const isEmployeeInfoValidationPassing = validateEmployeeInfo();
    const isBaseScheduleValidation = validateBaseSchedule();
    return isEmployeeInfoValidationPassing && isBaseScheduleValidation;
  };

  const handleCreateBaseScheduleClicked = () => {
    setIsValidationLoading(true);

    if (areAllValidationsPassing()) {
      setIsValidationLoading(false);
      setIsSubmitDialogOpen(true);
    } else {
      setIsValidationLoading(false);
    }
  };
  //#endregion

  //#region Event Handlers
  const handleClickDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    props.setIsDialogOpen(false);
  };

  const handleWorkgroupInputChange = async (event) => {
    setWorkgroup(event.target.value);
  };

  const handleStartDateChange = (date) => {
    startDateRef.current = date;
    setStartDate(date);
    validateDates();
  };

  const handleEndDateChange = (date) => {
    endDateRef.current = date;
    setEndDate(date);
    validateDates();
  };

  const handleCommentsChange = (event) => {
    setComments(event.target.value);
  };

  const handleCommentsBlur = () => {
    validateComments();
  };

  const handleApplyToAll = () => {
    let currentSchedule = copyJsonObject(baseSchedule);
    for (const day in currentSchedule) {
      if (!currentSchedule[day].isNA && !currentSchedule[day].isOff) {
        currentSchedule[day].start.hr = applyToAllFilterData.start.hr;
        currentSchedule[day].start.min = applyToAllFilterData.start.min;
        currentSchedule[day].end.hr = applyToAllFilterData.end.hr;
        currentSchedule[day].end.min = applyToAllFilterData.end.min;
        currentSchedule[day].position = applyToAllFilterData.position;
        currentSchedule[day].assignment = applyToAllFilterData.assignment;
        currentSchedule[day].isOff = false;
        currentSchedule[day].isNA = false;
      }
    }
    setBaseSchedule(copyJsonObject(currentSchedule));
    setBaseScheduleErrors(copyJsonObject(baseScheduleErrorsInitialState));
  };

  const handleApplyToAllStartHourChange = (value) => {
    let currentFilter = copyJsonObject(applyToAllFilterData);
    currentFilter.start.hr = value;
    setApplyToAllFilterData(currentFilter);
  };

  const handleApplyToAllStartMinChange = (value) => {
    let currentFilter = copyJsonObject(applyToAllFilterData);
    currentFilter.start.min = value;
    setApplyToAllFilterData(currentFilter);
  };

  const handleApplyToAllEndHourChange = (value) => {
    let currentFilter = copyJsonObject(applyToAllFilterData);
    currentFilter.end.hr = value;
    setApplyToAllFilterData(currentFilter);
  };

  const handleApplyToAllEndMinChange = (value) => {
    let currentFilter = copyJsonObject(applyToAllFilterData);
    currentFilter.end.min = value;
    setApplyToAllFilterData(currentFilter);
  };

  const handleApplyToAllPositionChange = (value) => {
    let currentFilter = copyJsonObject(applyToAllFilterData);
    currentFilter.position = value;
    setApplyToAllFilterData(currentFilter);
  };

  const handleApplyToAllAssignmentChange = (value) => {
    let currentFilter = copyJsonObject(applyToAllFilterData);
    currentFilter.assignment = value;
    setApplyToAllFilterData(currentFilter);
  };

  const handleCheckedShiftOff = (event, day) => {
    let currentSchedule = copyJsonObject(baseSchedule);
    if (event.target.checked) {
      currentSchedule[day].start.hr = "OFF";
      currentSchedule[day].start.min = "OFF";
      currentSchedule[day].end.hr = "OFF";
      currentSchedule[day].end.min = "OFF";
      currentSchedule[day].position = "OFF";
      currentSchedule[day].assignment = "OFF";
      currentSchedule[day].isOff = true;
    } else {
      currentSchedule[day].start.hr = "";
      currentSchedule[day].start.min = "";
      currentSchedule[day].end.hr = "";
      currentSchedule[day].end.min = "";
      currentSchedule[day].position = "Float";
      currentSchedule[day].assignment = "Float";
      currentSchedule[day].isOff = false;
    }
    setBaseSchedule(currentSchedule);
  };

  const handleStartHourChange = (day, value) => {
    let currentSchedule = copyJsonObject(baseSchedule);
    currentSchedule[day].start.hr = value;
    setBaseSchedule(currentSchedule);
  };

  const handleStartMinChange = (day, value) => {
    let currentSchedule = copyJsonObject(baseSchedule);
    currentSchedule[day].start.min = value;
    setBaseSchedule(currentSchedule);
  };

  const handleEndHourChange = (day, value) => {
    let currentSchedule = copyJsonObject(baseSchedule);
    currentSchedule[day].end.hr = value;
    setBaseSchedule(currentSchedule);
  };

  const handleEndMinChange = (day, value) => {
    let currentSchedule = copyJsonObject(baseSchedule);
    currentSchedule[day].end.min = value;
    setBaseSchedule(currentSchedule);
  };

  const handlePositionChange = (day, value) => {
    let currentSchedule = copyJsonObject(baseSchedule);
    currentSchedule[day].position = value;
    setBaseSchedule(currentSchedule);
  };

  const handleAssignmentChange = (day, value) => {
    let currentSchedule = copyJsonObject(baseSchedule);
    currentSchedule[day].assignment = value;
    setBaseSchedule(currentSchedule);
  };

  const handleClearBaseSchedule = () => {
    let currentSchedule = copyJsonObject(baseSchedule);
    for (const day in currentSchedule) {
      if (!currentSchedule[day].isNA) {
        currentSchedule[day].start.hr = "";
        currentSchedule[day].start.min = "";
        currentSchedule[day].end.hr = "";
        currentSchedule[day].end.min = "";
        currentSchedule[day].position = "Float";
        currentSchedule[day].assignment = "Float";
        currentSchedule[day].isOff = false;
        currentSchedule[day].isNA = false;
      }
    }

    setApplyToAllFilterData(copyJsonObject(baseScheduleApplyToAllFilterInitialState));
    setBaseSchedule(copyJsonObject(currentSchedule));
    setBaseScheduleErrors(copyJsonObject(baseScheduleErrorsInitialState));
  };

  const handleYesButtonClicked = () => {
    let baseSchedulePayload = {
      eid: props.employeeId,
      station: props.station,
      workgroup: workgroup,
      startDate: getIsoDateFormat(startDate),
      endDate: getIsoDateFormat(endDate),
      comments: comments,
      schedule: copyJsonObject(baseSchedule),
      modifiedByEid: userSession.eid,
      modifiedByLastName: userSession.lastName,
      modifiedByFirstName: userSession.firstName,
    };

    setIsSubmitLoading(true);
    addBaseSchedule(baseSchedulePayload)
      .then((response) => {
        setIsSubmitLoading(false);
        setIsSubmitDialogOpen(false);
        if (response.status === 200) {
          setSuccessDialogError("");
        } else {
          setSuccessDialogError("Error creating base schedule. Please try again.");
          setIsSuccessDialogError(true);
        }
        setIsSuccessDialogOpen(true);
      })
      .catch((error) => {
        console.log(error);
        setIsSubmitLoading(false);
        setIsSubmitDialogOpen(false);
        setSuccessDialogError("Error creating base schedule. Please try again.");
        setIsSuccessDialogError(true);
        setIsSuccessDialogOpen(true);
      });
  };
  //#endregion

  return (
    <Dialog
      className="detail-dialog"
      data-testid="custom-base-schedule-dialog"
      fullWidth
      maxWidth="lg"
      open={props.isDialogOpen}
      TransitionComponent={SlideUpTransition}
      keepMounted
      aria-labelledby="alert-dialog-slide-title"
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: "23px", fontWeight: "bold" }}>Custom Base Schedule</Typography>
          <DialogActions>
            <Button variant="outlined" color="primary" sx={{ marginTop: "-10px" }} onClick={handleClickDialogClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              sx={{ marginTop: "-10px", marginRight: "-10px" }}
              onClick={() => handleCreateBaseScheduleClicked()}
              disabled={isValidationLoading}
            >
              {isValidationLoading ? <CircularProgress size={25} /> : "Create"}
            </Button>
          </DialogActions>
        </Box>
      </DialogTitle>
      <DialogContent className="detail-dialog-content">
        <Typography sx={{ fontSize: "20px", fontWeight: "bold", marginBottom: "15px" }}>
          {props.employeeLastName + ", " + props.employeeFirstName}
        </Typography>

        <Grid container>
          <TextField
            className={classes.inputStation}
            label={STATION}
            variant="outlined"
            value={props.station}
            disabled
          />
          <FormControl>
            <StarsSelectInput
              disabled={props.isWorkgroupInputDisabled}
              label="Workgroup"
              menuMessage="Workgroup"
              value={workgroup}
              allOptions={ALL_WORKGROUP_DEFAULT_FILTERS}
              errors=""
              sortComparator={sortOnCaseSensitiveTexts}
              onChange={(event) => handleWorkgroupInputChange(event)}
              className={classes.inputSelect}
            />
          </FormControl>
          <Grid item>
            <StarsDatePicker
              label="Start Date"
              date={startDate}
              onChange={handleStartDateChange}
              dateErrors={employeeInfoErrors.startDateErrors}
              className={classes.inputSelect}
              minDate={convert_Luxon_To_DateFns(TODAY(props.station))}
              maxDate={convert_Luxon_To_DateFns(THREE_MONTHS_IN_FUTURE(props.station))}
            />
          </Grid>
          <Grid item>
            <StarsDatePicker
              label="End Date"
              date={endDate}
              onChange={handleEndDateChange}
              dateErrors={employeeInfoErrors.endDateErrors}
              className={classes.inputSelect}
              minDate={convert_Luxon_To_DateFns(TODAY(props.station))}
              maxDate={convert_Luxon_To_DateFns(THREE_MONTHS_IN_FUTURE(props.station))}
            />
          </Grid>
        </Grid>

        <TextField
          value={comments}
          variant="outlined"
          fullWidth
          onChange={handleCommentsChange}
          onBlur={handleCommentsBlur}
          label="Comments (Mandatory)"
          sx={{ marginTop: "20px" }}
          error={!!employeeInfoErrors.commentErrors}
          helperText={employeeInfoErrors.commentErrors}
        />

        {comments.length > FREEFORM_REASON_CHAR_LIMIT ? (
          <Box sx={{ textAlign: "right", marginRight: "14px", marginBottom: "20px", color: "primary.swaRed" }}>
            {comments.length} / {FREEFORM_REASON_CHAR_LIMIT}
          </Box>
        ) : (
          <Box sx={{ textAlign: "right", marginRight: "14px", marginBottom: "20px" }}>
            {comments.length} / {FREEFORM_REASON_CHAR_LIMIT}
          </Box>
        )}

        <BaseSchedule
          baseSchedule={baseSchedule}
          positions={positions}
          applyToAllFilterData={applyToAllFilterData}
          baseScheduleErrors={baseScheduleErrors}
          handleStartHourChange={handleStartHourChange}
          handleStartMinChange={handleStartMinChange}
          handleEndHourChange={handleEndHourChange}
          handleEndMinChange={handleEndMinChange}
          handlePositionChange={handlePositionChange}
          handleAssignmentChange={handleAssignmentChange}
          handleClearBaseSchedule={handleClearBaseSchedule}
          handleCheckedShiftOff={handleCheckedShiftOff}
          handleApplyToAll={handleApplyToAll}
          handleApplyToAllPositionChange={handleApplyToAllPositionChange}
          handleApplyToAllAssignmentChange={handleApplyToAllAssignmentChange}
          handleApplyToAllStartHourChange={handleApplyToAllStartHourChange}
          handleApplyToAllStartMinChange={handleApplyToAllStartMinChange}
          handleApplyToAllEndHourChange={handleApplyToAllEndHourChange}
          handleApplyToAllEndMinChange={handleApplyToAllEndMinChange}
        />
      </DialogContent>

      {isSubmitDialogOpen ? (
        <ConfirmationDialog
          isDialogOpen={isSubmitDialogOpen}
          title="Confirm"
          message={CREATE_BASE_SCHEDULE_CONFIRMATION_MESSAGE(
            props.employeeLastName,
            props.employeeFirstName,
            props.employeeId
          )}
          hasWarning={true}
          warningMessage={BASE_SCHEDULE_OVERWRITE_WARNING_MESSAGE}
          warningTitle={WARNING_ALERT_TITLE}
          handleNoButtonClicked={() => setIsSubmitDialogOpen(false)}
          handleYesButtonClicked={handleYesButtonClicked}
          isYesLoading={isSubmitLoading}
        />
      ) : (
        ""
      )}

      {isSuccessDialogOpen ? (
        <SuccessDialog
          isSuccessDialogOpen={isSuccessDialogOpen}
          title="Success"
          message={"Base Schedule has been successfully created."}
          handleOKClick={() => {
            setIsSuccessDialogOpen(false);
            props.setIsDialogOpen(false);
          }}
          hasError={isSuccessDialogError}
          error={successDialogError}
        />
      ) : (
        ""
      )}
    </Dialog>
  );
};

CustomBaseScheduleDialog.propTypes = {
  isDialogOpen: PropTypes.bool.isRequired,
  setIsDialogOpen: PropTypes.func.isRequired,
  isWorkgroupInputDisabled: PropTypes.bool.isRequired,
  station: PropTypes.string.isRequired,
  workgroup: PropTypes.string.isRequired,
  employeeFirstName: PropTypes.string.isRequired,
  employeeLastName: PropTypes.string.isRequired,
  employeeId: PropTypes.string.isRequired,
};

export default CustomBaseScheduleDialog;
